// EmployeeReviews.jsx
// Replace the MyReviews function in EmployeeDashboard.jsx with:
//   import EmployeeReviews from './EmployeeReviews'
// and in the nav section:
//   {nav === 'reviews' && <EmployeeReviews t={t} profile={empProfile || profile} pdrs={pdrs} />}

import { useState, useEffect, useRef } from 'react'
import { get, post, put, patch, del } from '../lib/api'
import ReviewReport from './ReviewReport'

const STATUS_MAP = {
  draft:             { bg: '#F1F5F9', color: '#64748B',  label: 'Draft — Not Submitted', icon: '💾' },
  submitted:         { bg: '#FFFBEB', color: '#D97706',  label: 'Submitted — Awaiting Review', icon: '⏳' },
  employer_reviewed: { bg: '#EFF6FF', color: '#4F46E5',  label: 'Reviewed — Sign Off Required', icon: '✍️' },
  disputed:          { bg: '#FFF7ED', color: '#D97706',  label: 'Disputed — Awaiting Resolution',  icon: '⚠️' },
  signed_off:        { bg: '#ECFDF5', color: '#059669',  label: 'Signed Off', icon: '✅' },
}

const EVIDENCE_TYPES = [
  { value: 'file',   label: '📎 Upload file (max 300 KB)', placeholder: '' },
  { value: 'drive',  label: '📁 Google Drive folder', placeholder: 'https://drive.google.com/drive/folders/...' },
  { value: 'pdf',    label: '📄 PDF document',         placeholder: 'https://...' },
  { value: 'image',  label: '🖼️ Image link',           placeholder: 'https://...' },
  { value: 'link',   label: '🔗 Other link',            placeholder: 'https://...' },
]

function emptyEvidence() {
  return { type: 'file', url: '', label: '' }
}

const GOAL_COLORS = [
  { border: '#4F46E5', bg: '#EEF2FF', badgeBg: 'linear-gradient(135deg,#4F46E5,#6366F1)', text: '#4338CA', soft: '#E0E7FF', planned: '#EEF2FF' },
  { border: '#059669', bg: '#ECFDF5', badgeBg: 'linear-gradient(135deg,#059669,#10B981)', text: '#047857', soft: '#D1FAE5', planned: '#ECFDF5' },
  { border: '#D97706', bg: '#FFFBEB', badgeBg: 'linear-gradient(135deg,#D97706,#F59E0B)', text: '#B45309', soft: '#FEF3C7', planned: '#FFFBEB' },
  { border: '#7C3AED', bg: '#F5F3FF', badgeBg: 'linear-gradient(135deg,#7C3AED,#8B5CF6)', text: '#6D28D9', soft: '#EDE9FE', planned: '#F5F3FF' },
  { border: '#DB2777', bg: '#FDF2F8', badgeBg: 'linear-gradient(135deg,#DB2777,#EC4899)', text: '#BE185D', soft: '#FCE7F3', planned: '#FDF2F8' },
]

// ════════════════════════════════════════════════════════════════════════════
// ROOT
// ════════════════════════════════════════════════════════════════════════════
export default function EmployeeReviews({ t, profile, pdrs, preselectedPdrId, onClearPreselect, onRefresh }) {
  const [view, setView]                   = useState('list')
  const [reviews, setReviews]             = useState([])
  const [selectedReview, setSelectedReview] = useState(null)
  const [loading, setLoading]             = useState(true)
  const [showReport, setShowReport]       = useState(false)
  const [reportReview, setReportReview]   = useState(null)
  const [withdrawingId, setWithdrawingId] = useState(null)
  const [withdrawing, setWithdrawing]     = useState(false)
  const [editingDraftId, setEditingDraftId] = useState(null)

  useEffect(() => { fetchReviews() }, [])

  // Auto-open create view when coming from PDR "Submit for Review"
  useEffect(() => {
    if (preselectedPdrId && !loading) {
      setView('create')
      if (onClearPreselect) onClearPreselect()
    }
  }, [preselectedPdrId, loading])

  useEffect(() => { fetchReviews() }, [])

  async function fetchReviews() {
    setLoading(true)
    try {
      const res = await get('/reviews')
      setReviews(res.reviews || [])
    } catch (e) { console.error(e.message) }
    setLoading(false)
  }

  async function handleWithdraw(reviewId) {
    setWithdrawing(true)
    try { await del(`/reviews/${reviewId}`) } catch (e) { console.error(e.message) }
    setWithdrawingId(null)
    setWithdrawing(false)
    fetchReviews()
  }

  async function openDetail(review) {
    try {
      const res = await get(`/reviews/${review.id}`)
      setSelectedReview(res.review)
    } catch (e) { console.error(e.message) }
    setView('detail')
  }

  const activePdrs     = pdrs.filter(p => p.status === 'active')
  const pendingSignOff = reviews.filter(r => r.status === 'employer_reviewed').length
  // Block entirely if ANY review is still in-flight (not yet signed off)
  const hasActiveReview  = reviews.some(r => r.status === 'submitted' || r.status === 'employer_reviewed')
  // Per-PDR: even signed-off reviews block resubmission for that PDR
  const reviewedPdrIds   = new Set(reviews.filter(r => r.status !== 'draft').map(r => r.pdr_id).filter(Boolean))
  const draftByPdrId     = Object.fromEntries(reviews.filter(r => r.status === 'draft' && r.pdr_id).map(r => [r.pdr_id, r]))
  const availablePdrs    = activePdrs.filter(p => !reviewedPdrIds.has(p.id))

  if (view === 'create') return (
    <CreateReview t={t} profile={profile} activePdrs={availablePdrs}
      preselectedPdrId={preselectedPdrId}
      draftReviewId={editingDraftId}
      onDone={() => { setEditingDraftId(null); fetchReviews(); setView('list') }}
      onCancel={() => { setEditingDraftId(null); setView('list') }} />
  )

  if (view === 'detail' && selectedReview) return (
    <ReviewDetail t={t} review={selectedReview} profile={profile}
      onBack={() => { fetchReviews(); setView('list') }}
      onRefresh={async () => {
        const res = await get(`/reviews/${selectedReview.id}`)
        setSelectedReview(res.review)
        fetchReviews()
      }} />
  )

  if (showReport && reportReview) return (
    <ReviewReport review={reportReview} employee={profile}
      onClose={() => { setShowReport(false); setReportReview(null) }} />
  )

  // ── LIST ──
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>My Reviews</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>Performance reviews linked to your approved PDR goals</p>
        </div>
        {availablePdrs.length > 0 || hasActiveReview ? (
          hasActiveReview ? (
            <div style={{ padding: '10px 16px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#D97706', maxWidth: 300, textAlign: 'right', lineHeight: 1.5 }}>
              ⏳ Review already in progress<br/>
              <span style={{ fontSize: 12, fontWeight: 400 }}>Withdraw it or sign off before submitting a new one.</span>
            </div>
          ) : availablePdrs.length > 0 ? (
            <button onClick={() => setView('create')}
              style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui', boxShadow: '0 2px 8px rgba(79,70,229,0.3)' }}>
              + Submit Review
            </button>
          ) : (
            <div style={{ padding: '10px 16px', background: '#F1F5F9', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 13, color: '#64748B', maxWidth: 260, textAlign: 'right', lineHeight: 1.5 }}>
              All PDR cycles already have a submitted review.
            </div>
          )
        ) : null}
      </div>

      {/* Show only one draft per PDR — most recent */}
      {Object.values(reviews.filter(r => r.status === 'draft').reduce((acc, dr) => {
        const key = dr.pdr_id || dr.id
        if (!acc[key] || new Date(dr.updated_at || dr.created_at) > new Date(acc[key].updated_at || acc[key].created_at)) acc[key] = dr
        return acc
      }, {})).map(dr => (
        <div key={dr.id} style={{ background:'#F8F9FA', border:'1px solid #CBD5E1', borderLeft:'4px solid #64748B', borderRadius:12, padding:'12px 16px', marginBottom:12, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:18 }}>💾</span>
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:'#475569' }}>Draft in progress — not yet submitted</div>
              <div style={{ fontSize:12, color:'#94A3B8' }}>Last saved {new Date(dr.updated_at || dr.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</div>
            </div>
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => { setEditingDraftId(dr.id); setView('create') }}
              style={{ padding:'7px 16px', background:'#4F46E5', border:'none', borderRadius:8, fontSize:12, fontWeight:700, color:'#fff', cursor:'pointer', fontFamily:'Inter,system-ui', whiteSpace:'nowrap' }}>
              ✏️ Edit Draft
            </button>
            <button onClick={async () => {
              if (!window.confirm('Delete this draft?')) return
              await del(`/reviews/${dr.id}`)
              fetchReviews()
            }}
              style={{ padding:'7px 12px', background:'transparent', border:'1px solid #FECACA', borderRadius:8, fontSize:12, fontWeight:700, color:'#EF4444', cursor:'pointer', fontFamily:'Inter,system-ui' }}>
              🗑 Delete
            </button>
          </div>
        </div>
      ))}

      {pendingSignOff > 0 && (
        <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 12, padding: '14px 18px', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
          <span style={{ fontSize: 22 }}>✍️</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#4F46E5' }}>
              {pendingSignOff} review{pendingSignOff > 1 ? 's' : ''} awaiting your sign-off
            </div>
            <div style={{ fontSize: 12, color: '#4F46E5', opacity: 0.8 }}>Your manager has completed their assessment — click to review and sign off</div>
          </div>
        </div>
      )}

      {loading ? (
        <p style={{ color: t.textMuted }}>Loading…</p>
      ) : reviews.length === 0 ? (
        <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '4rem 2rem', textAlign: 'center', boxShadow: t.shadow }}>
          <div style={{ fontSize: 52, marginBottom: '1rem' }}>⭐</div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: t.text, marginBottom: 8 }}>No reviews yet</h3>
          <p style={{ fontSize: 14, color: t.textMuted, maxWidth: 380, margin: '0 auto 1.5rem', lineHeight: 1.6 }}>
            {activePdrs.length > 0
              ? 'Submit your first review against your active PDR goals. Your manager will score each goal and provide feedback.'
              : 'You need an active, approved PDR cycle before submitting a review.'}
          </p>
          {activePdrs.length > 0 && (
            <button onClick={() => setView('create')}
              style={{ padding: '12px 28px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
              Submit first review →
            </button>
          )}
        </div>
      ) : reviews.map(review => {
        const s = STATUS_MAP[review.status] || STATUS_MAP.submitted
        const entries = Object.values(
    (review.review_goal_entries || []).reduce((acc, e) => {
      const key = e.pdr_goal_id || e.goal_number
      if (!acc[key] || e.id > acc[key].id) acc[key] = e
      return acc
    }, {})
  ).sort((a, b) => a.goal_number - b.goal_number)
        const needsAction = review.status === 'employer_reviewed'
        const isSignedOff = review.status === 'signed_off'
        const employerStarted = (review.review_goal_entries || []).some(e => e.employer_score != null && e.employer_score !== '')
        const canWithdraw = review.status === 'submitted' && !employerStarted
        const isWithdrawing = withdrawingId === review.id
        const avgScore = entries.filter(e => e.employer_score).length
          ? (entries.reduce((sum, e) => sum + (e.employer_score || 0), 0) / entries.filter(e => e.employer_score).length).toFixed(1)
          : null

        return (
          <div key={review.id}
            style={{ background: t.card, border: `2px solid ${needsAction ? '#BFDBFE' : isSignedOff ? '#A7F3D0' : t.cardBorder}`, borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: 14, boxShadow: t.shadow }}>

            {/* Withdraw confirm overlay */}
            {isWithdrawing && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#DC2626', marginBottom: 4 }}>⚠️ Withdraw this review?</div>
                <div style={{ fontSize: 13, color: '#7F1D1D', marginBottom: 12, lineHeight: 1.5 }}>
                  This will permanently delete your submission. You can resubmit a new one at any time.
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => handleWithdraw(review.id)} disabled={withdrawing}
                    style={{ padding: '8px 18px', background: '#EF4444', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, color: '#fff', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                    {withdrawing ? 'Withdrawing…' : 'Yes, withdraw'}
                  </button>
                  <button onClick={() => setWithdrawingId(null)}
                    style={{ padding: '8px 18px', background: 'transparent', border: `1px solid #FECACA`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: '#DC2626', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10, cursor: 'pointer' }}
              onClick={() => openDetail(review)}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: t.text, margin: '0 0 4px' }}>
                  {review.review_period || `Review ${review.year || ''}`}
                </h3>
                <div style={{ fontSize: 12, color: t.textMuted }}>
                  {entries.length} goal{entries.length !== 1 ? 's' : ''} · Submitted {new Date(review.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, background: s.bg, color: s.color, padding: '5px 14px', borderRadius: 99, border: `1px solid ${s.color}30`, flexShrink: 0 }}>
                {s.icon} {s.label}
              </span>
            </div>

            {avgScore && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 10, cursor: 'pointer' }} onClick={() => openDetail(review)}>
                <span style={{ fontSize: 26, fontWeight: 900, color: '#4F46E5' }}>{avgScore}</span>
                <span style={{ fontSize: 13, color: t.textMuted }}>/10 avg score</span>
              </div>
            )}

            {needsAction && (
              <div style={{ fontSize: 13, fontWeight: 600, color: '#4F46E5', display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, cursor: 'pointer' }} onClick={() => openDetail(review)}>
                ✍️ Manager has reviewed — tap to view feedback &amp; sign off
              </div>
            )}

            {/* Action buttons row */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8, paddingTop: 10, borderTop: `1px solid ${t.border}` }}>
              <button onClick={() => openDetail(review)}
                style={{ padding: '7px 14px', background: `${t.primary}14`, border: `1px solid ${t.primary}30`, borderRadius: 8, fontSize: 12, fontWeight: 600, color: t.primary, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                View details →
              </button>
              {isSignedOff && (
                <button onClick={() => { setReportReview(review); setShowReport(true) }}
                  style={{ padding: '7px 14px', background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#059669', cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                  📊 View Report
                </button>
              )}
              {canWithdraw && !isWithdrawing && (
                <button onClick={e => { e.stopPropagation(); setWithdrawingId(review.id) }}
                  style={{ padding: '7px 14px', background: 'transparent', border: '1px solid #FECACA', borderRadius: 8, fontSize: 12, fontWeight: 600, color: '#EF4444', cursor: 'pointer', fontFamily: 'Inter,system-ui', marginLeft: 'auto' }}>
                  Withdraw
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// CREATE REVIEW — multi-step
// ════════════════════════════════════════════════════════════════════════════
function CreateReview({ t, profile, activePdrs, onDone, onCancel, preselectedPdrId, draftReviewId }) {
  // If editing a draft: start at step 2, show loading until DB fetch completes
  const [step, setStep]                   = useState(draftReviewId ? 2 : 1)
  const [isInitializing, setIsInitializing] = useState(!!draftReviewId)
  const [selectedPdrId, setSelectedPdrId] = useState('')
  const [reviewPeriod, setReviewPeriod]   = useState('')
  const [draftId, setDraftId]             = useState(draftReviewId || null)
  const [goals, setGoals]                 = useState([])
  const [entries, setEntries]             = useState([])
  const [loading, setLoading]   = useState(false)
  const [savedMsg, setSavedMsg] = useState('')
  const [error, setError]       = useState('')
  const [showErrors, setShowErrors] = useState(false)
  const autoSaveTimer = useRef(null)
  const saveRef       = useRef(null)  // always points to latest save fn

  const selectedPdr = activePdrs.find(p => p.id === selectedPdrId)

  // Single reliable save — DELETE all entries then re-INSERT fresh
  // Called explicitly: score click, manual Save button
  saveRef.current = async (currentEntries, currentDraftId, currentPdrId, currentPeriod) => {
    const ents  = currentEntries  || entries
    const rId0  = currentDraftId  || draftId
    const pdrId = currentPdrId    || selectedPdrId
    const per   = currentPeriod   || reviewPeriod
    if (!pdrId || ents.length === 0) return null

    setSavedMsg('💾 Saving…')
    let rId = rId0

    try {
      // Create draft review if it doesn't exist yet
      if (!rId) {
        const { review } = await post('/reviews', {
          pdr_id: pdrId, review_period: per, year: new Date().getFullYear(),
          status: 'draft', submitted_at: new Date().toISOString(),
        })
        rId = review.id
        setDraftId(rId)
      } else {
        await patch(`/reviews/${rId}`, { review_period: per })
      }

      // Replace all entries
      const entries = ents.map(entry => ({
        pdr_goal_id: entry.pdr_goal_id,
        goal_number: entry.goal_number, goal_title: entry.goal_title,
        goal_description: entry.goal_description, kpi: entry.kpi,
        weightage: entry.weightage, what_was_done: entry.what_was_done,
        employee_score: entry.employee_score || null,
        evidence_links: entry.evidence_links,
      }))
      await put(`/reviews/${rId}/entries`, { entries })
    } catch (e) {
      setSavedMsg('❌ Save failed — ' + e.message)
      return null
    }

    const now = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    setSavedMsg(`✓ Saved ${now}`)
    setTimeout(() => setSavedMsg(''), 3000)
    return rId
  }

  useEffect(() => {
    if (draftReviewId) {
      initFromDraft(draftReviewId)
    } else if (preselectedPdrId) {
      setSelectedPdrId(preselectedPdrId)
      loadPdrGoals(preselectedPdrId)
    }
  }, [])

  async function initFromDraft(reviewId) {
    // Fetch everything fresh from the API using just the review ID
    let review
    try { ({ review } = await get(`/reviews/${reviewId}`)) } catch { setIsInitializing(false); return }
    if (!review) { setIsInitializing(false); return }

    setSelectedPdrId(review.pdr_id || '')
    setDraftId(reviewId)
    if (review.review_period) setReviewPeriod(review.review_period)

    let gs = []
    try { const { pdr } = await get(`/pdrs/${review.pdr_id}`); gs = pdr.goals || [] } catch { /* ignore */ }
    const de = review.review_goal_entries || []
    setGoals(gs)
    const dm = Object.fromEntries(de.map(e => [e.pdr_goal_id, e]))
    setEntries(gs.map(g => {
      const sv = dm[g.id]
      return {
        pdr_goal_id: g.id, goal_number: g.goal_number, goal_title: g.title,
        goal_description: g.description || '', kpi: g.kpi || '', weightage: g.weightage,
        what_was_done: sv?.what_was_done || '',
        employee_score: sv?.employee_score || null,
        evidence_links: sv?.evidence_links?.length ? sv.evidence_links : [emptyEvidence()],
      }
    }))
    setIsInitializing(false)
  }

  async function loadPdrGoals(pdrId) {
    // Always fetch fresh from the API — never rely on stale prop
    let gs = []
    let allReviews = []
    try {
      const [pdrRes, revRes] = await Promise.all([get(`/pdrs/${pdrId}`), get('/reviews')])
      gs = pdrRes.pdr?.goals || []
      allReviews = revRes.reviews || []
    } catch { /* ignore */ }
    setGoals(gs)
    const draft = allReviews.find(r => r.pdr_id === pdrId && r.status === 'draft')
    if (draft) {
      setDraftId(draft.id)
      if (draft.review_period) setReviewPeriod(draft.review_period)
      const de = draft.review_goal_entries || []
      const dm = Object.fromEntries(de.map(e => [e.pdr_goal_id, e]))
      setEntries(gs.map(g => {
        const sv = dm[g.id]
        return {
          pdr_goal_id: g.id, goal_number: g.goal_number, goal_title: g.title,
          goal_description: g.description || '', kpi: g.kpi || '', weightage: g.weightage,
          what_was_done: sv?.what_was_done || '',
          employee_score: sv?.employee_score || null,
          evidence_links: sv?.evidence_links?.length ? sv.evidence_links : [emptyEvidence()],
        }
      }))
    } else {
      setDraftId(null)
      setEntries(gs.map(g => ({
        pdr_goal_id: g.id, goal_number: g.goal_number, goal_title: g.title,
        goal_description: g.description || '', kpi: g.kpi || '', weightage: g.weightage,
        what_was_done: '', employee_score: null, evidence_links: [emptyEvidence()],
      })))
    }
  }

  function updateEntry(i, field, value) {
    const updated = [...entries]
    updated[i] = { ...updated[i], [field]: value }
    setEntries(updated)
  }

  function addEvidence(i) {
    const updated = [...entries]
    updated[i].evidence_links = [...updated[i].evidence_links, emptyEvidence()]
    setEntries(updated)
  }

  function removeEvidence(i, ei) {
    const updated = [...entries]
    updated[i].evidence_links = updated[i].evidence_links.filter((_, idx) => idx !== ei)
    setEntries(updated)
  }

  function updateEvidence(i, ei, field, value) {
    const updated = [...entries]
    updated[i].evidence_links[ei] = { ...updated[i].evidence_links[ei], [field]: value }
    setEntries(updated)
  }

  async function uploadEvidenceFile(i, ei, file) {
    if (!file) return
    if (file.size > 300 * 1024) { alert('File must be under 300 KB'); return }
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) { alert('Only JPG, PNG, WebP or PDF'); return }
    const dataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader()
      r.onload = () => resolve(r.result)
      r.onerror = reject
      r.readAsDataURL(file)
    })
    const updated = [...entries]
    updated[i].evidence_links[ei] = { ...updated[i].evidence_links[ei], url: dataUrl, label: updated[i].evidence_links[ei].label || file.name }
    setEntries(updated)
  }

  async function handleSubmit() {
    setError('')
    const unfilled = entries.filter(e => !e.what_was_done.trim())
    if (unfilled.length > 0) {
      return setError(`Please fill in "What I accomplished" for all ${unfilled.length} goal${unfilled.length > 1 ? 's' : ''}`)
    }

    // At least one piece of evidence (an uploaded file OR a link) per goal
    const missingEvidence = entries.filter(e => !(e.evidence_links || []).some(ev => ev.url && ev.url.trim()))
    if (missingEvidence.length > 0) {
      return setError(
        `Please attach evidence (a file or a link) for ${missingEvidence.length === entries.length ? 'all goals' : `Goal${missingEvidence.length > 1 ? 's' : ''} ${missingEvidence.map(e => e.goal_number).join(', ')}`} before submitting.`
      )
    }

    setLoading(true)

    let review
    try {
      if (draftId) {
        // Promote existing draft to submitted
        ({ review } = await patch(`/reviews/${draftId}`, {
          status: 'submitted', submitted_at: new Date().toISOString(), review_period: reviewPeriod,
        }))
      } else {
        ({ review } = await post('/reviews', {
          pdr_id:       selectedPdrId,
          review_period: reviewPeriod,
          year:         new Date().getFullYear(),
          status:       'submitted',
          submitted_at: new Date().toISOString(),
        }))
      }

      const entries2 = entries.map(e => ({
        pdr_goal_id:      e.pdr_goal_id,
        goal_number:      e.goal_number,
        goal_title:       e.goal_title,
        goal_description: e.goal_description,
        kpi:              e.kpi,
        weightage:        e.weightage,
        what_was_done:    e.what_was_done,
        evidence_links:   e.evidence_links.filter(ev => ev.url.trim()),
      }))
      await put(`/reviews/${review.id}/entries`, { entries: entries2 })
    } catch (e) { setError(e.message); setLoading(false); return }

    setLoading(false)
    onDone()
  }

  const IS = { display: 'block', width: '100%', padding: '11px 14px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }
  const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }

  return (
    <div>
      {/* Header */}
      <div style={{ background: t.white, borderBottom: `1px solid ${t.border}`, padding: '0 0 1.5rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>Submit Performance Review</h1>
          <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>Review your achievements against your PDR goals</p>
        </div>
        {/* Steps */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {['Select PDR', 'Goal Evidence', 'Review & Submit'].map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 24, height: 24, borderRadius: '50%', background: step > i + 1 ? '#10B981' : step === i + 1 ? '#4F46E5' : `${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: step >= i + 1 ? '#fff' : t.textMuted }}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: step === i + 1 ? t.text : t.textMuted }}>{s}</span>
              </div>
              {i < 2 && <div style={{ width: 24, height: 1, background: t.border }} />}
            </div>
          ))}
        </div>
      </div>

      {/* Loading draft */}
      {isInitializing && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'4rem', flexDirection:'column', gap:12 }}>
          <div style={{ fontSize:32 }}>⏳</div>
          <p style={{ fontSize:14, color:t.textMuted, fontFamily:'Inter,system-ui' }}>Loading your draft…</p>
        </div>
      )}

      {/* ── STEP 1 — Select PDR ── */}
      {!isInitializing && step === 1 && (
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '2rem', boxShadow: t.shadow }}>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={LS}>Select your active PDR cycle *</label>
              <select value={selectedPdrId}
                onChange={e => { setSelectedPdrId(e.target.value); if (e.target.value) loadPdrGoals(e.target.value) }}
                style={{ ...IS, cursor: 'pointer' }}>
                <option value="">— Choose a PDR cycle —</option>
                {activePdrs.map(p => (
                  <option key={p.id} value={p.id}>{p.title} ({p.review_type} · {p.start_date} → {p.end_date})</option>
                ))}
              </select>
            </div>

            {selectedPdr && (
              <div style={{ background: `${t.primary}08`, border: `1px solid ${t.primary}20`, borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.primary, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>PDR Goals to review</div>
                {goals.map(g => (
                  <div key={g.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: `1px solid ${t.border}` }}>
                    <span style={{ fontSize: 13, color: t.text, fontWeight: 500 }}>Goal {g.goal_number}: {g.title}</span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: t.primary }}>{g.weightage}%</span>
                  </div>
                ))}
              </div>
            )}

            <div>
              <label style={LS}>Review period label</label>
              <input value={reviewPeriod} onChange={e => setReviewPeriod(e.target.value)}
                placeholder="e.g. Q1 2026 · Jan–Mar" style={IS} />
              <p style={{ fontSize: 12, color: t.textMuted, margin: '6px 0 0' }}>Optional — helps identify this review in your history</p>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${t.border}` }}>
            <button onClick={onCancel} style={{ padding: '11px 24px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 14, fontWeight: 500, color: t.textSub, cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
              ← Cancel
            </button>
            <button onClick={() => {
              if (!selectedPdrId || goals.length === 0) return setError('Please select a PDR cycle first')
              // Date check: warn if PDR hasn't ended yet
              const pdr = activePdrs.find(p => p.id === selectedPdrId)
              if (pdr && pdr.end_date && new Date(pdr.end_date) > new Date()) {
                const daysLeft = Math.ceil((new Date(pdr.end_date) - new Date()) / (1000 * 60 * 60 * 24))
                if (!window.confirm(`⚠️ This PDR cycle ends in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Are you sure you want to submit a review now?`)) return
              }
              setError('')
              setStep(2)
            }} style={{ padding: '11px 28px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
              Continue →
            </button>
          </div>
          {error && <div style={{ fontSize: 13, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginTop: 12 }}>⚠️ {error}</div>}
        </div>
      )}

      {/* ── STEP 2 — Goal Evidence ── */}
      {step === 2 && (
        <div>
          {/* Progress dots */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.5rem' }}>
            <span style={{ fontSize: 13, color: t.textMuted }}>
              {entries.filter(e => e.what_was_done.trim()).length} of {entries.length} goals filled
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              {entries.map((e, i) => {
                const c = GOAL_COLORS[i % GOAL_COLORS.length]
                return (
                  <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: e.what_was_done.trim() ? c.border : t.border, transition: 'background .2s' }} />
                )
              })}
            </div>
          </div>

          {entries.map((entry, i) => {
            const c = GOAL_COLORS[i % GOAL_COLORS.length]
            const isDone = !!entry.what_was_done.trim()
            return (
            <div key={i} style={{ borderRadius: 16, marginBottom: 24, overflow: 'hidden', boxShadow: t.shadow, border: `1px solid ${c.border}40`, borderLeft: `5px solid ${c.border}` }}>

              {/* Coloured goal header */}
              <div style={{ background: t.dark ? `${c.border}18` : c.bg, padding: '14px 20px', borderBottom: `1px solid ${c.border}25`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: c.badgeBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 900, color: '#fff', flexShrink: 0, boxShadow: `0 2px 8px ${c.border}50` }}>
                    {entry.goal_number}
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: t.dark ? '#fff' : c.text }}>{entry.goal_title}</div>
                    <div style={{ fontSize: 11, color: t.dark ? 'rgba(255,255,255,0.5)' : c.text, opacity: 0.7, marginTop: 1 }}>Goal {entry.goal_number} of {entries.length}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {isDone && <span style={{ fontSize: 11, fontWeight: 700, background: '#ECFDF5', color: '#059669', padding: '3px 8px', borderRadius: 99 }}>✓ Filled</span>}
                  <span style={{ fontSize: 13, fontWeight: 800, background: t.dark ? `${c.border}30` : c.soft, color: c.text, padding: '5px 14px', borderRadius: 99, border: `1px solid ${c.border}30` }}>
                    {entry.weightage}%
                  </span>
                </div>
              </div>

              <div style={{ background: t.card, padding: '1.5rem' }}>
                {/* What was planned — colour-tinted read-only box */}
                <div style={{ background: t.dark ? `${c.border}10` : c.planned, border: `1px solid ${c.border}25`, borderRadius: 10, padding: '1rem', marginBottom: '1.25rem' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: c.text, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, opacity: 0.8 }}>📋 What was planned</div>
                  {entry.goal_description && (
                    <p style={{ fontSize: 13, color: t.text, margin: '0 0 8px', lineHeight: 1.6 }}>{entry.goal_description}</p>
                  )}
                  {entry.kpi && (
                    <p style={{ fontSize: 13, color: t.textSub, margin: 0, padding: '6px 10px', background: t.dark ? 'rgba(255,255,255,0.05)' : '#fff', borderRadius: 6, border: `1px solid ${c.border}20` }}>
                      <strong style={{ color: c.text }}>KPI:</strong> {entry.kpi}
                    </p>
                  )}
                </div>

                {/* What was done */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ ...LS, color: showErrors && !entry.what_was_done.trim() ? '#EF4444' : c.text }}>
                    ✅ What I accomplished *
                    {showErrors && !entry.what_was_done.trim() && (
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', marginLeft: 8, background: '#FEF2F2', padding: '2px 8px', borderRadius: 99 }}>Required — please fill this in</span>
                    )}
                  </label>
                  <textarea value={entry.what_was_done}
                    onChange={e => { updateEntry(i, 'what_was_done', e.target.value); if (showErrors) setShowErrors(false) }}
                    placeholder={`Describe what you actually achieved for "${entry.goal_title}"…`}
                    rows={4}
                    style={{ ...IS, resize: 'vertical',
                      borderColor: showErrors && !entry.what_was_done.trim() ? '#EF4444'
                        : entry.what_was_done.trim() ? `${c.border}60` : t.inputBorder,
                      boxShadow: showErrors && !entry.what_was_done.trim() ? '0 0 0 3px rgba(239,68,68,0.15)' : 'none',
                      outline: 'none' }}
                    onFocus={e => e.target.style.borderColor = showErrors && !entry.what_was_done.trim() ? '#EF4444' : c.border}
                    onBlur={e => e.target.style.borderColor = showErrors && !entry.what_was_done.trim() ? '#EF4444' : entry.what_was_done.trim() ? `${c.border}60` : t.inputBorder}
                  />
                </div>

                {/* Evidence */}
                <div>
                  <label style={{ ...LS, color: c.text }}>
                    📁 Evidence &amp; Supporting Documents
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', marginLeft: 6 }}>★ Google Drive folder required</span>
                  </label>
                  <p style={{ fontSize: 12, color: t.textMuted, margin: '0 0 10px', lineHeight: 1.5 }}>
                    A Google Drive folder link is <strong>mandatory</strong> for every goal. Share a folder containing your reports, screenshots and files. You can add extra PDF or image links below.
                  </p>

                  {entry.evidence_links.map((ev, ei) => {
                    const isDrive = ev.type === 'drive'
                    const isMissingDrive = isDrive && !ev.url.trim()
                    return (
                    <div key={ei} style={{ background: t.dark ? `${c.border}08` : c.planned, border: `1px solid ${isMissingDrive ? '#FCA5A5' : `${c.border}25`}`, borderRadius: 10, padding: '12px', marginBottom: 10, position: 'relative', boxShadow: isMissingDrive ? '0 0 0 3px rgba(239,68,68,0.1)' : 'none' }}>
                      {isDrive && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
                          ★ Required — paste your Google Drive folder link here
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 10, marginBottom: 8 }}>
                        <div>
                          <label style={{ ...LS, fontSize: 12 }}>Type</label>
                          <select value={ev.type} onChange={e => updateEvidence(i, ei, 'type', e.target.value)}
                            style={{ ...IS, fontSize: 13, padding: '8px 10px' }}>
                            {EVIDENCE_TYPES.map(et => (
                              <option key={et.value} value={et.value}>{et.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={{ ...LS, fontSize: 12 }}>Label / Description</label>
                          <input value={ev.label}
                            onChange={e => updateEvidence(i, ei, 'label', e.target.value)}
                            placeholder={isDrive ? 'e.g. Q1 Evidence Folder' : 'e.g. Sales Report PDF'}
                            style={{ ...IS, fontSize: 13, padding: '8px 10px' }} />
                        </div>
                      </div>
                      {ev.type === 'file' ? (
                        <div>
                          <label style={{ ...LS, fontSize: 12 }}>Attach file (image or PDF, max 300 KB)</label>
                          <input type="file" accept="image/*,application/pdf"
                            onChange={e => uploadEvidenceFile(i, ei, e.target.files[0])}
                            style={{ fontSize: 13, color: t.text }} />
                          {ev.url && <div style={{ fontSize: 12, color: '#059669', marginTop: 4 }}>✓ Attached{ev.label ? `: ${ev.label}` : ''}</div>}
                        </div>
                      ) : (
                        <div>
                          <label style={{ ...LS, fontSize: 12 }}>
                            URL / Link {isDrive && <span style={{ color: '#EF4444' }}>*</span>}
                          </label>
                          <input value={ev.url}
                            onChange={e => updateEvidence(i, ei, 'url', e.target.value)}
                            placeholder={EVIDENCE_TYPES.find(et => et.value === ev.type)?.placeholder || 'https://...'}
                            style={{ ...IS, fontSize: 13, padding: '8px 10px', borderColor: isMissingDrive ? '#FCA5A5' : IS.border }} />
                        </div>
                      )}
                      {entry.evidence_links.length > 1 && !isDrive && (
                        <button onClick={() => removeEvidence(i, ei)}
                          style={{ position: 'absolute', top: 10, right: 10, background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: t.textMuted, lineHeight: 1 }}>×</button>
                      )}
                    </div>
                    )
                  })}

                  <button onClick={() => addEvidence(i)}
                    style={{ width: '100%', padding: '10px', background: 'transparent', border: `2px dashed ${c.border}40`, borderRadius: 8, fontSize: 13, fontWeight: 600, color: c.text, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontFamily: 'Inter, system-ui' }}>
                    <span style={{ fontSize: 18 }}>+</span> Add another link
                  </button>
                </div>

                {/* Self-score — employer-style highlighted box at bottom */}
                <div style={{ background: t.dark ? `${c.border}15` : c.bg, border: `1px solid ${c.border}40`, borderRadius: 10, padding: '1rem 1.25rem', marginTop: '1.25rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 16, alignItems: 'start' }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: c.text, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 }}>⭐ My Self-Score for this Goal</div>
                      <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 10 }}>How would you rate your own performance on this goal?</div>
                      {entry.employee_score ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 52, height: 52, borderRadius: 10, background: `${entry.employee_score >= 8 ? '#059669' : entry.employee_score >= 6 ? '#4F46E5' : entry.employee_score >= 4 ? '#D97706' : '#EF4444'}15`, border: `2px solid ${entry.employee_score >= 8 ? '#059669' : entry.employee_score >= 6 ? '#4F46E5' : entry.employee_score >= 4 ? '#D97706' : '#EF4444'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 900, color: entry.employee_score >= 8 ? '#059669' : entry.employee_score >= 6 ? '#4F46E5' : entry.employee_score >= 4 ? '#D97706' : '#EF4444' }}>
                            {entry.employee_score}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: entry.employee_score >= 8 ? '#059669' : entry.employee_score >= 6 ? '#4F46E5' : entry.employee_score >= 4 ? '#D97706' : '#EF4444' }}>
                              {entry.employee_score >= 8 ? 'Outstanding' : entry.employee_score >= 6 ? 'Good' : entry.employee_score >= 4 ? 'Average' : 'Needs Improvement'}
                            </div>
                            <button type="button" onClick={() => updateEntry(i, 'employee_score', null)}
                              style={{ fontSize: 11, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2 }}>✕ Change score</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: t.textMuted, fontStyle: 'italic' }}>No score selected yet</div>
                      )}
                    </div>
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 5 }}>
                        {[1,2,3,4,5,6,7,8,9,10].map(score => {
                          const sel = entry.employee_score === score
                          const col = score >= 8 ? '#059669' : score >= 6 ? '#4F46E5' : score >= 4 ? '#D97706' : '#EF4444'
                          return (
                            <button key={score} type="button"
                              onClick={async () => {
                                const updated = entries.map((en, idx) => idx === i ? { ...en, employee_score: score } : en)
                                setEntries(updated)
                                const newDraftId = await saveRef.current(updated, draftId, selectedPdrId, reviewPeriod)
                                if (newDraftId && !draftId) setDraftId(newDraftId)
                              }}
                              style={{ padding: '9px 4px', borderRadius: 6, border: `2px solid ${sel ? col : t.border}`, background: sel ? col : t.card, color: sel ? '#fff' : t.textMuted, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
                              {score}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            )
          })}

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: `1px solid ${t.border}` }}>
            <button onClick={() => setStep(1)} style={{ padding: '11px 24px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 14, fontWeight: 500, color: t.textSub, cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>← Back</button>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              {savedMsg && (
                <span style={{ fontSize:12, color:'#10B981', fontWeight:600, display:'flex', alignItems:'center', gap:4 }}>
                  {savedMsg}
                </span>
              )}
              <button type="button"
                onClick={async () => {
                  const newId = await saveRef.current(entries, draftId, selectedPdrId, reviewPeriod)
                  if (newId && !draftId) setDraftId(newId)
                }}
                style={{ padding:'10px 18px', background: t.card, border:`1px solid ${t.border}`, borderRadius:8, fontSize:13, fontWeight:600, color:t.textSub, cursor:'pointer', fontFamily:'Inter,system-ui' }}>
                💾 Save
              </button>
              <button onClick={() => {
              const unfilled = entries.filter(e => !e.what_was_done.trim())
              if (unfilled.length > 0) {
                setShowErrors(true)
                return setError(`${unfilled.length} goal${unfilled.length > 1 ? 's' : ''} still need your "What I accomplished" filled in — highlighted below`)
              }
              setError('')
              setShowErrors(false)
              setStep(3)
            }} style={{ padding: '11px 28px', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>
              Review & Submit →
            </button>
            </div>
          </div>
          {error && <div style={{ fontSize: 13, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginTop: 12 }}>⚠️ {error}</div>}
        </div>
      )}

      {/* ── STEP 3 — Review & Submit ── */}
      {!isInitializing && step === 3 && (
        <div>
          <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow, marginBottom: '1.5rem' }}>
            <div style={{ marginBottom: '1.25rem', paddingBottom: '1.25rem', borderBottom: `1px solid ${t.border}` }}>
              <h3 style={{ fontSize: 17, fontWeight: 700, color: t.text, margin: '0 0 4px' }}>
                {reviewPeriod || selectedPdr?.title}
              </h3>
              <div style={{ fontSize: 12, color: t.textMuted }}>{entries.length} goals · {selectedPdr?.review_type}</div>
            </div>

            {entries.map((entry, i) => (
              <div key={i} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: `1px solid ${t.border}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#4F46E514', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#4F46E5' }}>{entry.goal_number}</div>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{entry.goal_title}</span>
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#4F46E5' }}>{entry.weightage}%</span>
                </div>
                <div style={{ background: t.dark ? 'rgba(255,255,255,0.04)' : '#F8F9FA', borderRadius: 8, padding: '10px 12px', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, marginBottom: 4 }}>ACCOMPLISHED</div>
                  <p style={{ fontSize: 13, color: t.text, margin: 0, lineHeight: 1.6 }}>{entry.what_was_done}</p>
                </div>
                {entry.evidence_links.filter(ev => ev.url.trim()).length > 0 && (
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {entry.evidence_links.filter(ev => ev.url.trim()).map((ev, ei) => (
                      <a key={ei} href={ev.url} target="_blank" rel="noreferrer"
                        style={{ fontSize: 11, fontWeight: 600, background: '#EFF6FF', color: '#4F46E5', padding: '3px 10px', borderRadius: 99, textDecoration: 'none', border: '1px solid #BFDBFE' }}>
                        {EVIDENCE_TYPES.find(et => et.value === ev.type)?.label.split(' ')[0]} {ev.label || ev.url.slice(0, 30) + '…'}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ background: `#4F46E508`, border: '1px solid #4F46E520', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
            <p style={{ fontSize: 13, color: t.textSub, margin: 0, lineHeight: 1.7 }}>
              By submitting, you confirm this accurately represents your performance against the agreed goals. Your manager will score each goal, leave comments, and return the review to you for sign-off.
            </p>
          </div>

          {error && <div style={{ fontSize: 13, color: '#EF4444', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '10px 14px', marginBottom: 12 }}>⚠️ {error}</div>}

          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1.5rem', borderTop: `1px solid ${t.border}` }}>
            <button onClick={() => setStep(2)} style={{ padding: '11px 24px', background: t.card, border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 14, fontWeight: 500, color: t.textSub, cursor: 'pointer', fontFamily: 'Inter, system-ui' }}>← Back</button>
            <button onClick={handleSubmit} disabled={loading}
              style={{ padding: '12px 32px', background: loading ? t.border : 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, color: loading ? t.textMuted : '#fff', cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'Inter, system-ui', boxShadow: loading ? 'none' : '0 2px 8px rgba(79,70,229,0.3)' }}>
              {loading ? 'Submitting…' : '🚀 Submit for Manager Review'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}


// ════════════════════════════════════════════════════════════════════════════
// DISPUTE OR SIGN-OFF — employee chooses to agree or raise concern
// ════════════════════════════════════════════════════════════════════════════
function DisputeOrSignOff({ t, review, onBack, onRefresh }) {
  const [mode, setMode]         = useState(null) // null | 'signoff' | 'dispute'
  const [comment, setComment]   = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState('')
  const [done, setDone]         = useState(false)

  const IS = { display:'block', width:'100%', padding:'11px 14px', background:t.input, border:`1px solid ${t.inputBorder}`, borderRadius:8, fontSize:14, color:t.inputText, fontFamily:'Inter,system-ui', boxSizing:'border-box', outline:'none', resize:'vertical' }
  const LS = { display:'block', fontSize:12, fontWeight:600, color:t.textSub, marginBottom:5 }

  async function submit() {
    if (!comment.trim()) return setError('Please add a comment before submitting.')
    setLoading(true); setError('')
    const isDispute = mode === 'dispute'
    const updatePayload = {
      status: isDispute ? 'disputed' : 'signed_off',
      employee_sign_off_comment: comment,
      signed_off_at: new Date().toISOString(),
    }
    try {
      await patch(`/reviews/${review.id}`, {
        ...updatePayload,
        share_token: Math.random().toString(36).slice(2,9) + Date.now().toString(36),
      })
      // Mark PDR completed only on sign-off, not dispute
      if (!isDispute && review.pdr_id) {
        await patch(`/pdrs/${review.pdr_id}`, { status: 'completed' })
      }
    } catch (e) { setError(e.message); setLoading(false); return }
    setLoading(false); setDone(true)
    onRefresh && onRefresh()
  }

  if (done) return (
    <div style={{ background: mode === 'dispute' ? '#FFF7ED' : '#ECFDF5', border: `2px solid ${mode === 'dispute' ? '#FDE68A' : '#A7F3D0'}`, borderRadius: 14, padding: '1.5rem', textAlign: 'center' }}>
      <div style={{ fontSize: 36, marginBottom: 8 }}>{mode === 'dispute' ? '⚠️' : '✅'}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: mode === 'dispute' ? '#D97706' : '#059669' }}>
        {mode === 'dispute' ? 'Dispute raised — manager will be notified' : 'Review signed off successfully'}
      </div>
      <button onClick={onBack} style={{ marginTop: 16, padding: '9px 20px', background: '#4F46E5', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'Inter,system-ui' }}>
        ← Back to Reviews
      </button>
    </div>
  )

  return (
    <div style={{ background: t.card, border: '2px solid #4F46E530', borderRadius: 14, padding: '1.5rem', boxShadow: t.shadow }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 6px' }}>✍️ Your Response</h3>
      <p style={{ fontSize: 13, color: t.textMuted, margin: '0 0 1.25rem', lineHeight: 1.6 }}>
        Do you agree with the scores and feedback given? Choose below.
      </p>

      {/* Choice buttons */}
      {!mode && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 0 }}>
          <button onClick={() => setMode('signoff')}
            style={{ padding: '1rem', background: '#ECFDF5', border: '2px solid #A7F3D0', borderRadius: 12, cursor: 'pointer', textAlign: 'center', fontFamily: 'Inter,system-ui' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#059669' }}>I Agree</div>
            <div style={{ fontSize: 12, color: '#047857', marginTop: 4 }}>Sign off and close the review</div>
          </button>
          <button onClick={() => setMode('dispute')}
            style={{ padding: '1rem', background: '#FFF7ED', border: '2px solid #FDE68A', borderRadius: 12, cursor: 'pointer', textAlign: 'center', fontFamily: 'Inter,system-ui' }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>⚠️</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#D97706' }}>I Disagree</div>
            <div style={{ fontSize: 12, color: '#92400E', marginTop: 4 }}>Raise a dispute for manager review</div>
          </button>
        </div>
      )}

      {/* Comment form after choosing */}
      {mode && (
        <>
          <div style={{ background: mode === 'dispute' ? '#FFF7ED' : '#ECFDF5', border: `1px solid ${mode === 'dispute' ? '#FDE68A' : '#A7F3D0'}`, borderRadius: 10, padding: '10px 14px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 20 }}>{mode === 'dispute' ? '⚠️' : '✅'}</span>
            <div style={{ fontSize: 13, fontWeight: 600, color: mode === 'dispute' ? '#D97706' : '#059669' }}>
              {mode === 'dispute' ? 'Raising a dispute — explain which scores you disagree with and why' : 'Signing off — add your acknowledgement comment'}
            </div>
            <button onClick={() => { setMode(null); setComment(''); setError('') }}
              style={{ marginLeft: 'auto', background: 'none', border: 'none', color: t.textMuted, cursor: 'pointer', fontSize: 18 }}>×</button>
          </div>
          <div style={{ marginBottom: '1rem' }}>
            <label style={LS}>{mode === 'dispute' ? 'Reason for dispute *' : 'Your acknowledgement comment *'}</label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4}
              placeholder={mode === 'dispute'
                ? 'e.g. I believe my score for Goal 2 should be higher. I completed 95% of the target and the remaining 5% was blocked by external dependencies…'
                : 'e.g. I agree with the feedback. I will focus on improving my documentation skills in the next quarter…'}
              style={IS} />
          </div>
          {error && <div style={{ fontSize:13, color:'#EF4444', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:8, padding:'10px 14px', marginBottom:12 }}>⚠️ {error}</div>}
          <button onClick={submit} disabled={loading || !comment.trim()}
            style={{ width:'100%', padding:'13px', background: loading ? t.border : mode === 'dispute' ? 'linear-gradient(135deg,#D97706,#F59E0B)' : 'linear-gradient(135deg,#10B981,#059669)', border:'none', borderRadius:8, fontSize:14, fontWeight:700, color: loading ? t.textMuted : '#fff', cursor: loading || !comment.trim() ? 'not-allowed' : 'pointer', fontFamily:'Inter,system-ui', opacity: !comment.trim() ? 0.7 : 1 }}>
            {loading ? 'Submitting…' : mode === 'dispute' ? '⚠️ Submit Dispute' : '✅ Sign Off & Complete Review'}
          </button>
        </>
      )}
    </div>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// REVIEW DETAIL — view submitted review + sign-off if employer has reviewed
// ════════════════════════════════════════════════════════════════════════════
function ReviewDetail({ t, review, profile, onBack, onRefresh }) {
  // sign-off handled by DisputeOrSignOff component
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const entries = Object.values(
    (review.review_goal_entries || []).reduce((acc, e) => {
      const key = e.pdr_goal_id || e.goal_number
      if (!acc[key] || e.id > acc[key].id) acc[key] = e
      return acc
    }, {})
  ).sort((a, b) => a.goal_number - b.goal_number)
  const s = STATUS_MAP[review.status] || STATUS_MAP.submitted
  const isReviewed = review.status === 'employer_reviewed'
  const isSignedOff = review.status === 'signed_off'

  const avgScore = entries.filter(e => e.employer_score).length
    ? (entries.reduce((sum, e) => sum + (e.employer_score || 0), 0) / entries.filter(e => e.employer_score).length).toFixed(1)
    : null

  function scoreColor(s) { return s >= 8 ? '#059669' : s >= 6 ? '#4F46E5' : s >= 4 ? '#D97706' : '#EF4444' }

  async function handleSignOff() {
    setError('')
    if (!signOffComment.trim()) return setError('Please add a comment before signing off.')
    setLoading(true)
    const updatePayload = {
      status: 'signed_off',
      employee_sign_off_comment: signOffComment,
      signed_off_at: new Date().toISOString(),
    }
    try {
      const shareToken = Math.random().toString(36).slice(2, 9) + Date.now().toString(36)
      await patch(`/reviews/${review.id}`, { ...updatePayload, share_token: shareToken })
      // Mark linked PDR as completed so employee can no longer edit goals or resubmit
      if (review.pdr_id) {
        await patch(`/pdrs/${review.pdr_id}`, { status: 'completed' })
      }
    } catch (e) { setError(e.message); setLoading(false); return }
    setLoading(false)
    onRefresh && onRefresh()
    onBack()
  }

  const IS = { display: 'block', width: '100%', padding: '11px 14px', background: t.input, border: `1px solid ${t.inputBorder}`, borderRadius: 8, fontSize: 14, color: t.inputText, fontFamily: 'Inter, system-ui', boxSizing: 'border-box', outline: 'none' }
  const LS = { display: 'block', fontSize: 13, fontWeight: 600, color: t.text, marginBottom: 6 }

  return (
    <div>
      <button onClick={onBack} style={{ fontSize: 13, color: t.textMuted, background: 'none', border: 'none', cursor: 'pointer', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui' }}>
        ← Back to reviews
      </button>

      {/* Review header */}
      <div style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: t.shadow }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>
              {review.review_period || `Review ${review.year || ''}`}
            </h2>
            <div style={{ fontSize: 13, color: t.textMuted }}>
              Submitted {new Date(review.submitted_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 12, fontWeight: 600, background: s.bg, color: s.color, padding: '5px 14px', borderRadius: 99, border: `1px solid ${s.color}30`, display: 'block', marginBottom: 8 }}>
              {s.icon} {s.label}
            </span>
            {avgScore && (
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 28, fontWeight: 900, color: scoreColor(parseFloat(avgScore)) }}>{avgScore}</span>
                <span style={{ fontSize: 13, color: t.textMuted }}>/10</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Sign-off banner */}
      {isReviewed && (
        <div style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #F0F0FF 100%)', border: '2px solid #BFDBFE', borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#4F46E5', marginBottom: 4 }}>✍️ Your manager has reviewed this submission</div>
          <div style={{ fontSize: 13, color: '#4F46E5', opacity: 0.8 }}>Review their scores and comments below, then add your sign-off comment at the bottom.</div>
        </div>
      )}

      {isSignedOff && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 14, padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#059669', marginBottom: 4 }}>✅ Review fully signed off</div>
          <div style={{ fontSize: 13, color: '#059669' }}>Signed off on {new Date(review.signed_off_at).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>
      )}

      {/* Per-goal entries */}
      {entries.map((entry, i) => (
        <div key={i} style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 14, marginBottom: 16, overflow: 'hidden', boxShadow: t.shadow }}>
          <div style={{ background: `linear-gradient(135deg, #4F46E508 0%, #6366F105 100%)`, padding: '12px 20px', borderBottom: `1px solid ${t.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #4F46E5 0%, #6366F1 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff' }}>{entry.goal_number}</div>
              <span style={{ fontSize: 15, fontWeight: 700, color: t.text }}>{entry.goal_title}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 700, background: '#4F46E514', color: '#4F46E5', padding: '3px 10px', borderRadius: 99 }}>{entry.weightage}% weight</span>
              {entry.employer_score && (
                <span style={{ fontSize: 14, fontWeight: 900, color: scoreColor(entry.employer_score) }}>{entry.employer_score}/10</span>
              )}
            </div>
          </div>

          <div style={{ padding: '1.25rem 1.5rem' }}>
            {/* Planned */}
            <div style={{ background: t.dark ? 'rgba(255,255,255,0.04)' : '#F8F9FA', borderRadius: 8, padding: '10px 12px', marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>📋 What was planned</div>
              {entry.goal_description && <p style={{ fontSize: 13, color: t.text, margin: '0 0 4px', lineHeight: 1.6 }}>{entry.goal_description}</p>}
              {entry.kpi && <p style={{ fontSize: 13, color: t.textSub, margin: 0 }}><strong>KPI:</strong> {entry.kpi}</p>}
            </div>

            {/* Done */}
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>✅ What I accomplished</div>
              <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{entry.what_was_done}</p>
            </div>

            {/* Evidence */}
            {(entry.evidence_links || []).filter(ev => ev.url).length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>📁 Evidence</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {entry.evidence_links.filter(ev => ev.url).map((ev, ei) => (
                    <a key={ei} href={ev.url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, fontWeight: 600, background: '#EFF6FF', color: '#4F46E5', padding: '6px 12px', borderRadius: 8, textDecoration: 'none', border: '1px solid #BFDBFE', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {EVIDENCE_TYPES.find(et => et.value === ev.type)?.label.split(' ')[0]} {ev.label || 'View link'}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Employer feedback */}
            {(isReviewed || isSignedOff) && (
              <div style={{ background: '#F0F0FF', border: '1px solid #C7D2FE', borderRadius: 10, padding: '1rem' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#4F46E5', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>💬 Manager Feedback</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <p style={{ fontSize: 13, color: t.text, margin: 0, lineHeight: 1.7, flex: 1 }}>{entry.employer_comment || 'No comment provided.'}</p>
                  {entry.employer_score && (
                    <div style={{ textAlign: 'center', flexShrink: 0 }}>
                      <div style={{ fontSize: 28, fontWeight: 900, color: scoreColor(entry.employer_score), lineHeight: 1 }}>{entry.employer_score}</div>
                      <div style={{ fontSize: 10, color: t.textMuted }}>/ 10</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Employer overall feedback */}
      {(isReviewed || isSignedOff) && (review.employer_final_comment || review.improvement_areas || review.final_score) && (
        <div style={{ background: t.card, border: '2px solid #C7D2FE', borderRadius: 14, padding: '1.5rem', marginBottom: '1.5rem', boxShadow: t.shadow }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: t.text, margin: '0 0 1.25rem' }}>📋 Manager's Overall Assessment</h3>

          {review.final_score && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.25rem', padding: '1rem', background: `${scoreColor(review.final_score)}10`, borderRadius: 10, border: `1px solid ${scoreColor(review.final_score)}30` }}>
              <div>
                <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 2 }}>FINAL SCORE</div>
                <div style={{ fontSize: 36, fontWeight: 900, color: scoreColor(review.final_score), lineHeight: 1 }}>
                  {review.final_score}<span style={{ fontSize: 16, fontWeight: 400 }}>/10</span>
                </div>
              </div>
              <div style={{ background: t.border, borderRadius: 99, height: 10, flex: 1 }}>
                <div style={{ width: `${review.final_score * 10}%`, height: '100%', background: scoreColor(review.final_score), borderRadius: 99 }} />
              </div>
            </div>
          )}

          {review.employer_final_comment && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: t.textMuted, marginBottom: 6 }}>Overall comment</div>
              <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{review.employer_final_comment}</p>
            </div>
          )}

          {review.improvement_areas && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#D97706', marginBottom: 6 }}>🎯 Areas for improvement</div>
              <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{review.improvement_areas}</p>
            </div>
          )}
        </div>
      )}

      {/* Employee sign-off OR dispute */}
      {isReviewed && (
        <DisputeOrSignOff t={t} review={review} onBack={onBack} onRefresh={onRefresh} />
      )}

      {/* Signed-off comment */}
      {isSignedOff && review.employee_sign_off_comment && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 14, padding: '1.25rem 1.5rem' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 6 }}>YOUR SIGN-OFF COMMENT</div>
          <p style={{ fontSize: 14, color: t.text, margin: 0, lineHeight: 1.7 }}>{review.employee_sign_off_comment}</p>
        </div>
      )}
    </div>
  )
}
